import React from 'react';
import { isFunction, isNotNil, shallowEqual } from '../../../util/object';
import { getDisplayNameForComponent } from '../../error/util';
import { DEV, STAGE } from '../../../constants';
import {
  MEASUREMENT_KEYS,
  playbackPerformanceReview,
} from '../../debug/measure/PerformanceReview';

/**
 * the redux store object, where we can dispatch from and getState and so on
 * @type {ReduxStore}
 */
let internalStore = null;

/**
 * Method to call to unsubscribe from internal store
 * @type {function}
 */
let internalStoreUnsubscribe = null;

/**
 * Reference to the most recent redux state
 * @type {ReduxState}
 */
let internalState = null;

/**
 * Singleton representation of the current frame.
 * @type {number}
 */
let internalCurrentFrame = 0;

/**
 * Current FPS calculation value
 * @type {number}
 */
let internalFPS = 0;

/**
 * Set of callback function that are called whenever the current frame, or the state, is updated
 * @type {Set<Function>}
 */
const subscribers = new Set();

/**
 * True if FPS measurement is allowed in the current environment
 * @type {boolean}
 */
const canUpdateFPS = DEV || STAGE;

/**
 * Number of frame ticks to measure for FPS average
 * @type {number}
 */
const FPS_SAMPLE_SIZE = 10;

/**
 * Array containing the last n frame tick time deltas
 * @type {number[]}
 */
const fpsSamples = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

/**
 * Variable that tracks the last time the frame was updated
 * @type {number}
 */
let lastFrameUpdate = performance.now();

/**
 * Current index in the fpsSamples array we are updating.
 * @type {number}
 */
let fpsSampleIndex = 0;

/**
 * Updates the FPS average by adding a new tick to the samples array then calculates the average.
 */
const updateFPS = () => {
  if (!canUpdateFPS) return;

  playbackPerformanceReview.measureInterval(MEASUREMENT_KEYS.FRAME_INTERVAL);
  const thisUpdate = performance.now();

  // Get the time for this tick and put in the array
  const fps = 1 / ((thisUpdate - lastFrameUpdate) / 1000);
  fpsSamples[fpsSampleIndex] = fps;
  fpsSampleIndex++;
  if (fpsSampleIndex >= FPS_SAMPLE_SIZE) {
    fpsSampleIndex = 0;
  }

  // Add the samples together
  let rollup = 0;
  for (let i = 0; i < FPS_SAMPLE_SIZE; i++) {
    rollup += fpsSamples[i];
  }

  // Average the samples
  const averageFps = rollup / FPS_SAMPLE_SIZE;
  lastFrameUpdate = thisUpdate;
  internalFPS = Math.round(averageFps);
};

/**
 * Notify all the callback functions in the subscribers set of the new currentFrame.
 */
const notifySubscribers = () => {
  subscribers.forEach((callback) => {
    callback(internalCurrentFrame, internalState, internalStore);
  });
};

/**
 * Set the current frame
 * @param {Number} frameNumber
 */
export function setCurrentFrame(frameNumber = 0) {
  internalCurrentFrame = frameNumber;
  notifySubscribers();
  updateFPS();
}

/**
 * Get the current frame.
 * @returns {number}
 */
export function getCurrentFrame() {
  return internalCurrentFrame;
}

/**
 * Get the current FPS average.
 * @returns {number}
 */
export function getFramesPerSecond() {
  return internalFPS;
}

/**
 * Set the store for internal use, then add change subscription to it.
 * Changes to the store cause a subscriber update
 * @param store
 */
export function setStore(store) {
  if (isNotNil(internalStore) && isFunction(internalStoreUnsubscribe)) {
    internalStoreUnsubscribe();
  }

  internalStore = store;
  internalState = internalStore.getState();
  internalStoreUnsubscribe = internalStore.subscribe(() => {
    internalState = store.getState();
    notifySubscribers();
  });
}

/**
 * Takes a callback method and adds it to the subscribers set.
 * @param callback
 */
export const subscribeToCurrentFrame = (callback) => {
  subscribers.add(callback);
};

/**
 * Removes a callback from the subscribers set.
 * @param callback
 */
export const unsubscribeFromCurrentFrame = (callback) => {
  subscribers.delete(callback);
};

/**
 * Clear all subscribers (useful in tests)
 */
export const clearAllSubscribers = () => {
  subscribers.clear();
};

/**
 * This is a function that can be used as an annotation or as a way to wrap React Components like so:
 *
 * connectCurrentFrame()
 * class MyComponent extends React.PureComponent {}
 *
 * if no arguments are provided to connectCurrentFrame then `currentFrame` is passed as a property to the
 * wrapped component. If a `mapFrameToProps` function is passed in, then `mapFrameToProps` is called with
 * the currentFrame, state, and the components own properties. This function should return an object with
 * key value pairs of props for the React component, similar to how `connect` from redux works. In this case,
 * `currentFrame` is NOT provided as a default.
 *
 * Annotations are run in order as they are stacked in the code so be sure to put this where you want it to be.
 *
 * @param {function} [mapFrameToProps]
 * @returns {function(*=): CurrentFrameWrapper}
 */
export function connectCurrentFrame(mapFrameToProps) {
  return (WrappedComponent) => {
    class CurrentFrameWrapper extends React.Component {
      _frameProps = null;

      constructor(props) {
        super(props);

        // set up frameProps
        this._frameProps = this.getMappedProps(props);
      }

      componentDidMount() {
        // Subscribe to frame changes
        subscribeToCurrentFrame(this.subscriptionHandler);
      }

      shouldComponentUpdate(nextProps) {
        // If props are shallow equal no need to do anything.
        if (shallowEqual(nextProps, this.props)) return false;

        // If mapFrameToProps relies on props, recalculate.
        if (isFunction(mapFrameToProps) && mapFrameToProps.length >= 3) {
          this._frameProps = this.getMappedProps(nextProps);
        }

        return true;
      }

      componentWillUnmount() {
        // Unsubscribe from frame changes
        unsubscribeFromCurrentFrame(this.subscriptionHandler);
      }

      render() {
        // Combine frameProps with the components other props.
        const finalProps = Object.assign(
          {
            ...this.props,
          },
          this._frameProps,
        );

        return <WrappedComponent {...finalProps} />;
      }

      getMappedProps = (props) => {
        if (isFunction(mapFrameToProps)) {
          return mapFrameToProps(internalCurrentFrame, internalState, props);
        }

        return {
          currentFrame: internalCurrentFrame,
        };
      };

      subscriptionHandler = () => {
        const mappedFrameProps = this.getMappedProps(this.props);
        if (shallowEqual(mappedFrameProps, this._frameProps)) return;

        this._frameProps = mappedFrameProps;
        this.forceUpdate();
      };
    }

    CurrentFrameWrapper.displayName = getDisplayNameForComponent(
      'CurrentFrameWrapper',
      WrappedComponent,
    );
    return CurrentFrameWrapper;
  };
}
